import type { PartialOptions } from 'overlayscrollbars'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { type AnimationEvent, Fragment, useId, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import type { AdminMenuSecondary, AdminMenuSection } from './AdminMenu'
import { AdminMenuItem } from './AdminMenuItem'
import type { AdminMenuHoverProps } from './AdminMenuRail'
import { isSectionVisible, isVisible } from './adminMenuUtils'
import {
  type AdminMenuActivation,
  type AdminMenuPanelContentAnimation,
  type AdminMenuPanelEntrance,
  type AdminMenuSelection,
  resolveContentAnimation,
} from './useAdminMenuExpansion'

const scrollbarOptions = {
  scrollbars: {
    theme: 'os-theme-navigation',
    autoHide: 'scroll',
    autoHideDelay: 800,
    clickScroll: true,
  },
  overflow: { x: 'hidden', y: 'scroll' },
} satisfies PartialOptions

type AdminMenuPanelPush = {
  entrance: AdminMenuPanelEntrance
  isLanding: boolean
  isExiting: boolean
}

type AdminMenuPanelProps = {
  sectionKey: string
  title: string
  secondary: AdminMenuSecondary
  onSelect: (selection: AdminMenuSelection) => void
  scrollOffsets: Map<string, number>
  activeKey?: string
  push?: AdminMenuPanelPush
  hoverProps?: AdminMenuHoverProps
}

type SectionListProps = {
  sections: AdminMenuSection[]
  onSelect: (activation: AdminMenuActivation) => void
  activeKey?: string
}

const SectionList = ({ sections, onSelect, activeKey }: SectionListProps) => {
  const titleId = useId()

  return sections.filter(isSectionVisible).map((section, index) => (
    <Fragment key={section.key}>
      {section.title ? (
        <h3
          id={`${titleId}-${section.key}`}
          className='m-0 truncate px-6 pt-4 pb-1.5 font-normal text-colorTextSecondary text-xs'
        >
          {section.title}
        </h3>
      ) : (
        index > 0 && <hr className='my-2 border-0 border-[#9db0df4d] border-t border-solid' />
      )}
      <ul
        className='m-0 flex list-none flex-col gap-0.5 px-2 py-0'
        aria-labelledby={section.title ? `${titleId}-${section.key}` : undefined}
      >
        {section.items.filter(isVisible).map(item => (
          <li key={item.key}>
            {item.link ? (
              <AdminMenuItem
                itemKey={item.key}
                label={item.label}
                icon={item.icon}
                isExpanded
                isLabelWrapped
                isActive={item.key === activeKey}
                isCurrentPage={item.key === activeKey}
                badgeValue={item.badgeValue}
                isNew={item.isNew}
                onNavigate={onSelect}
                link={item.link}
              />
            ) : (
              <AdminMenuItem
                itemKey={item.key}
                label={item.label}
                icon={item.icon}
                isExpanded
                isLabelWrapped
                isActive={item.active ?? false}
                isCurrentItem={item.active ?? false}
                badgeValue={item.badgeValue}
                isNew={item.isNew}
                onNavigate={onSelect}
                onClick={item.onClick}
              />
            )}
          </li>
        ))}
      </ul>
    </Fragment>
  ))
}

// The entrance stays on through the retract. Swapping it for animate-none cancels the animation,
// and returning within the exit hold would restart it from opacity 0 instead of easing the panel
// back from wherever the retract left it.
const pushClassName = (
  entrance: AdminMenuPanelEntrance,
  { isLanding, isExiting }: AdminMenuPanelPush
) =>
  twMerge(
    isExiting ? 'nav-panel-out' : 'nav-panel-push',
    entrance === 'glide' ? 'animate-nav-panel-glide' : 'animate-nav-panel-enter',
    isLanding ? 'start-18 animate-none' : 'start-67 shadow-navigation',
    'motion-reduce:animate-none'
  )

type ShownContent = {
  sectionKey: string
  animation: AdminMenuPanelContentAnimation
}

const useContentAnimation = (sectionKey: string, isEntering: boolean) => {
  const [shown, setShown] = useState<ShownContent>({ sectionKey, animation: 'none' })

  const hasSectionChanged = shown.sectionKey !== sectionKey
  if (hasSectionChanged) {
    setShown({ sectionKey, animation: resolveContentAnimation({ hasSectionChanged, isEntering }) })
  }

  return shown.animation
}

export const AdminMenuPanel = ({
  sectionKey,
  title,
  secondary,
  onSelect,
  scrollOffsets,
  activeKey,
  push,
  hoverProps,
}: AdminMenuPanelProps) => {
  // The entrance is picked when the panel mounts, swapping sections must not replay it
  const [mountedEntrance] = useState(push?.entrance)
  const [isEntering, setIsEntering] = useState(Boolean(push))
  const contentAnimation = useContentAnimation(sectionKey, isEntering)
  const footerSections = (secondary.footerSections ?? []).filter(isSectionVisible)
  const selectInSection = (activation: AdminMenuActivation) =>
    onSelect({ ...activation, sectionKey, origin: 'panel' })

  const trackOwnAnimation = (isRunning: boolean) => (event: AnimationEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      setIsEntering(isRunning)
    }
  }

  return (
    <div
      {...hoverProps}
      inert={push?.isExiting}
      onAnimationStart={trackOwnAnimation(true)}
      onAnimationEnd={trackOwnAnimation(false)}
      className={twMerge(
        'navigation-surface absolute inset-y-2 start-18 z-10 flex w-65 flex-col rounded-2xl py-2',
        push && pushClassName(mountedEntrance ?? push.entrance, push)
      )}
    >
      <div
        key={sectionKey}
        className={twMerge(
          'flex min-h-0 flex-1 flex-col gap-6',
          contentAnimation === 'swap' && 'animate-nav-panel-swap motion-reduce:animate-none',
          contentAnimation === 'fade' && 'animate-nav-panel-fade motion-reduce:animate-none'
        )}
      >
        <h2 className='m-0 flex h-12 shrink-0 items-center px-6 font-semibold text-base text-colorText'>
          <span className='truncate'>{title}</span>
        </h2>
        <div className='flex min-h-0 flex-1 flex-col'>
          <OverlayScrollbarsComponent
            options={scrollbarOptions}
            events={{
              initialized: instance => {
                instance.elements().viewport.scrollTop = scrollOffsets.get(sectionKey) ?? 0
              },
              scroll: instance => {
                scrollOffsets.set(sectionKey, instance.elements().viewport.scrollTop)
              },
            }}
            className={twMerge('min-h-0 flex-1', footerSections.length > 0 && 'pb-2')}
          >
            <SectionList
              sections={secondary.sections}
              activeKey={activeKey}
              onSelect={selectInSection}
            />
          </OverlayScrollbarsComponent>
          {footerSections.length > 0 && (
            <div className='shrink-0'>
              <hr className='mt-0 mb-2 border-0 border-[#9db0df4d] border-t border-solid' />
              <SectionList
                sections={footerSections}
                activeKey={activeKey}
                onSelect={selectInSection}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
